import CustomInputs from './Inputs';
import { FORM_CONTROLFIELD_TYPES } from '../../constants/constants';
import { useError } from '../../utils/context/ErrorContext';
import { GetImages } from '../../utils/GetImages';
import NewSelectAuto from '../tenants/NewSelectAuto';
import AddTraspButton from '../firstTimeUserExperience/commonCom/AddTraspButton';
import React, { useEffect, useRef, useState } from 'react';
import Checkbox from '../../newComponents/form-fields/checkbox';
import apiHelper from '../../utils/apiHelper';
import { LeaseAgreementService } from '@propertelligent/client-api';
import { TextCss } from '../GlobalCss/TextCss';

const DESCRIPTION_MAX_LENGTH = 250;

export interface DynamicAddMoreFormProps {
  data: any;
  setData: any;
  id?: any;
  showNonRefundable?: boolean;
  onDeletePayment?: (payment: any) => void | Promise<void>;
}

const DynamicAddMoreForm: React.FC<DynamicAddMoreFormProps> = ({ data, setData, id = undefined, showNonRefundable = false, onDeletePayment }) => {
  const { errors, setErrors, errorMessages, setErrorMessages, clearErrors } = useError();
  const [paymentTypes, setPaymentTypes] = useState([])
  const dataArray = Array.isArray(data) ? data : data ? [data] : [];
  // Grace Period/Description used to silently block further input past their caps
  // (99 days / 250 chars) with zero visual feedback -- mirrors InputField's existing
  // transient-error-flash pattern (bug/ui-bugfix-batch4 item 7) instead.
  const transientErrorTimeoutsRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    return () => {
      Object.values(transientErrorTimeoutsRef.current).forEach(clearTimeout);
    };
  }, []);

  const showTransientFieldError = (errorKey: string, message: string) => {
    if (transientErrorTimeoutsRef.current[errorKey]) {
      clearTimeout(transientErrorTimeoutsRef.current[errorKey]);
    }
    setErrors((prev) => ({ ...prev, [errorKey]: true }));
    setErrorMessages((prev) => ({ ...prev, [errorKey]: message }));
    transientErrorTimeoutsRef.current[errorKey] = setTimeout(() => {
      setErrors((prev) => ({ ...prev, [errorKey]: false }));
      setErrorMessages((prev) => ({ ...prev, [errorKey]: "" }));
    }, 1000);
  };

  const handleDeletePayment = async (paymentOrId) => {
    if (Array.isArray(dataArray) && dataArray.length <= 1) return;
    const payment = typeof paymentOrId === 'object' && paymentOrId !== null ? paymentOrId : dataArray.find(p => p?.id === paymentOrId);
    const idToRemove = payment?.id ?? paymentOrId;
    if (onDeletePayment && payment) {
      await Promise.resolve(onDeletePayment(payment));
    } else {
      setData(dataArray.filter(p => p?.id !== idToRemove));
    }
  };

  const handleChange = (id, field, value, index) => {
    if (Array.isArray(dataArray)) {
      setData(dataArray?.map(payment =>
        payment?.id === id ? { ...payment, [field]: value } : payment
      ));
      const errorKey = `${field}-${index}`;
      setErrors((prevErrors) => ({ ...prevErrors, [errorKey]: false }));
      setErrorMessages((prev) => ({ ...prev, [errorKey]: "" }));
    }
  };
  const getPaymentTypes = () => {
    apiHelper(LeaseAgreementService.getApiLeaseAgreementPaymentTypes, { showNotification: false })
      .then((res) => {
        if (res?.data?.success) {
          setPaymentTypes(res?.data?.success)
        }
      }).catch((err) => { })
  }

  useEffect(() => {
    getPaymentTypes()
  }, [])

  // Don't let the user add another blank row while an existing one is still missing
  // required details -- validate every current row and surface each field's own message
  // (mirrors NewSelectAuto/CustomInputs' own error-prop display, no submit/touch gating
  // needed here since this form isn't react-hook-form-based).
  const validateExistingRowsBeforeAdd = (): boolean => {
    const newErrors: Record<string, boolean> = { ...errors };
    const newErrorMessages: Record<string, string> = { ...errorMessages };
    let hasError = false;

    dataArray.forEach((payment, index) => {
      if (!payment?.paymentType) {
        newErrors[`paymentType-${index}`] = true;
        newErrorMessages[`paymentType-${index}`] = "Payment Type is required";
        hasError = true;
      }
      if (!payment?.amount) {
        newErrors[`amount-${index}`] = true;
        newErrorMessages[`amount-${index}`] = "Amount is required";
        hasError = true;
      }
      if (!payment?.dueDate) {
        newErrors[`dueDate-${index}`] = true;
        newErrorMessages[`dueDate-${index}`] = "Due date is required";
        hasError = true;
      }
      if (payment?.gracePeriods === undefined || payment?.gracePeriods === null || payment?.gracePeriods === "") {
        newErrors[`gracePeriods-${index}`] = true;
        newErrorMessages[`gracePeriods-${index}`] = "Grace period is required";
        hasError = true;
      }
      if (!payment?.description) {
        newErrors[`description-${index}`] = true;
        newErrorMessages[`description-${index}`] = "Description is required";
        hasError = true;
      }
    });

    if (hasError) {
      setErrors((prev) => ({ ...prev, ...newErrors }));
      setErrorMessages((prev) => ({ ...prev, ...newErrorMessages }));
    }
    return !hasError;
  };

  const handleAddPayment = () => {
    if (!validateExistingRowsBeforeAdd()) return;
    setData([...data,
    {
      id: data.length + 1,
      paymentType: "",
      amount: "",
      dueDate: "",
      gracePeriods: "",
      description: "",
      isNonRefundable: false,
    },
    ]);
  };

  return (
    <>
      <div className='dynamicAddMoreForm '>
        {Array.isArray(dataArray) && dataArray?.length > 0 ? (
          dataArray?.map((payment, index) => (<>
            <div className='dynamicAddMoreFormInput' key={payment?.id}>
              <div key={payment?.id} className="dynamicAddMoreForminputHeader  basic-form-fivefield  ">
                <NewSelectAuto
                  disabled={undefined}
                  setDisabled={undefined}
                  selected={payment?.paymentType}
                  setSelected={(value) => handleChange(payment?.id, 'paymentType', value, index)}
                  setSelectedOption={undefined}
                  required={true}
                  selectedOption={undefined}
                  data={paymentTypes}
                  title={"Wait till the data fetches"}
                  type="Payment Type"
                  label={index === 0 ? "Payment Type" : ''}
                  setError={() => { }}
                  error={errors[`paymentType-${index}`]}
                  errorMessage={errorMessages[`paymentType-${index}`]}
                  fromContractor={false}
                  width={"100%"}
                />
                <CustomInputs
                  name={"amount"}
                  labelText={index === 0 ? "Amount" : ""}
                  type={FORM_CONTROLFIELD_TYPES.TEXT}
                  value={payment?.amount ? payment?.amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""}
                  isFormControl={true}
                  currencySymbol="$"
                  required={true}
                  onChange={(e) => {
                    let value = e.target.value.replace(/,/g, "");
                    value = value.replace(/^0+/, "");

                    if (value === "" || /^(\d+(\.\d{0,2})?)?$/.test(value)) {
                      handleChange(payment?.id, "amount", value, index);
                    }
                  }}
                  onBlur={() => {
                    if (payment?.amount) {
                      const formattedAmount = parseFloat(payment.amount).toFixed(2);
                      handleChange(payment?.id, "amount", formattedAmount, index);
                    }
                  }}
                  placeholder="Enter Amount"
                  myClassName="containerTextField normal_text"
                  isError={errors[`amount-${index}`]}
                  errorMessages={errorMessages[`amount-${index}`]}
                  containerClassName="w-100"
                  isCurrency={true}
                />
                <CustomInputs
                  name={"startDate"}
                  labelText={index === 0 ? 'Due Date' : ''}
                  type={FORM_CONTROLFIELD_TYPES.DATE}
                  isFormControl={true}
                  required={true}
                  value={payment?.dueDate || null}
                  onChange={(e) => {
                    handleChange(payment?.id, 'dueDate', e.target.value, index)
                  }}
                  placeholder="Select a Date"
                  myClassName="containerTextField normal_text"
                  isError={errors[`dueDate-${index}`]}
                  errorMessages={errorMessages[`dueDate-${index}`]}
                  containerClassName="w-100"
                />
                <CustomInputs
                  name={"graceperiods"}
                  labelText={index === 0 ? 'Grace Period' : ''}
                  type={FORM_CONTROLFIELD_TYPES.NUMBER}
                  value={payment?.gracePeriods}
                  isFormControl={true}
                  required={true}
                  currencySymbol="Days"
                  onChange={(e) => {
                    let value = e.target.value;
                    if (/^\d*$/.test(value)) {
                      const numericValue = parseInt(value, 10) || 0;
                      if (numericValue <= 99) {
                        handleChange(payment?.id, "gracePeriods", value, index);
                      } else {
                        showTransientFieldError(`gracePeriods-${index}`, "Grace period cannot exceed 99 days");
                      }
                    }
                  }}
                  placeholder="0"
                  myClassName="containerTextField normal_text"
                  isError={errors[`gracePeriods-${index}`]}
                  errorMessages={errorMessages[`gracePeriods-${index}`]}
                  containerClassName="w-100"
                />
                {/* CustomInputs only ever renders an <input>, so a real resizable
                    "Expand"-able textarea (matching Notes/AddPayment's DescriptionTextarea)
                    isn't possible through it; that shared component also depends on
                    react-hook-form (useFormContext), which this plain-controlled-value form
                    doesn't use, ruling out reusing DescriptionTextarea directly here without
                    a much larger RHF migration. Rendering a scoped <textarea> for just this
                    field keeps the same visual language (custom_input/lablelClass/
                    errorMessageStyles) as its CustomInputs siblings. */}
                <div className="w-100 custom_input">
                  {index === 0 && (
                    <div className="lablelClass">
                      <p className={TextCss.black.sub_heading_600_14}>
                        Description<span className="red">*</span>
                      </p>
                    </div>
                  )}
                  <textarea
                    className={`${errors[`description-${index}`] ? "errorBorder errorOutline" : "normalBorder normalOutline"} ${TextCss.sub_heading_400_14} inputClass form-control containerTextField normal_text`}
                    rows={1}
                    value={payment?.description || ""}
                    placeholder="Enter Description"
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length > DESCRIPTION_MAX_LENGTH && value.length >= (payment?.description?.length ?? 0)) {
                        showTransientFieldError(`description-${index}`, "Description cannot exceed 250 characters");
                        return;
                      }
                      handleChange(payment?.id, 'description', value, index);
                    }}
                  />
                  <div className="d-flex justify-content-between align-items-start">
                    {errors[`description-${index}`] ? (
                      <p className="errorMessageStyles py-1 m-0">{errorMessages[`description-${index}`]}</p>
                    ) : <span />}
                    <span className="sub_text grey_subtext1" style={{ fontSize: '12px' }}>
                      {(payment?.description || "").length}/{DESCRIPTION_MAX_LENGTH}
                    </span>
                  </div>
                </div>
              </div>
              {dataArray?.length > 1 && (
                <div className='delete-payment-icon'>
                  {index === 0 && <div className="delete-payment-icon-spacer" />}
                  <img src={GetImages.DeleteRedBg} className='Icon32 cursore-pointer' alt='' onClick={() => handleDeletePayment(payment.id)} />
                </div>
              )}
            </div>
            {!showNonRefundable &&
              <div className="my-2">
                <Checkbox
                  isChecked={payment?.isNonRefundable}
                  onChange={() =>
                    handleChange(
                      payment?.id,
                      "isNonRefundable",
                      !payment?.isNonRefundable,
                      index
                    )
                  }
                  label="Is Non-Refundable"
                />
              </div>
            }
          </>
          ))
        ) : (
          <p>No data available</p>
        )}
        {!id ? (
          <div className=''>
            <AddTraspButton
              type="button"
              bname={"Add More"}
              onClick={handleAddPayment}
              border={true}
              width="fit-content"
              disabled={dataArray?.length === 4}
              className={`addtrasparent ${dataArray?.length === 4 ? "disabled" : ""}`}
              textclass={dataArray?.length === 4 ? "normal_text fw-600" : "normal_text fw-600 blue clickable"}
            />
          </div>
        ) : ""
        }
      </div>

    </>
  );
};

export default DynamicAddMoreForm;


